import { createContext, ReactNode, useContext } from "react"
import { useIdleTimer } from "react-idle-timer"
import { useNavigate } from "react-router-dom"
import { useLocalStorage } from "customerAttachments/hooks/useLocalStorage"

import { loginApi, LoginReq, LoginRes, logoutApi } from "./api"

interface AuthContextProps {
  user: LoginRes
  login: (payload: LoginReq) => Promise<LoginRes>
  logout: () => void
}

type AuthContextPropsWithNull = AuthContextProps | null

const AuthContext = createContext<AuthContextPropsWithNull>(null)

export const CUSTOMER_ATTACHMENTS_USER_KEYS = "customer_attachment_user"

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useLocalStorage(CUSTOMER_ATTACHMENTS_USER_KEYS, null)
  const navigate = useNavigate()

  const login = async ({ account, password }: LoginReq) => {
    const data = await loginApi({ account, password })
    setUser(data)
    return data
  }

  const logout = async () => {
    try {
      await logoutApi()
      navigate("/login", { replace: true })
      setUser(null)
    } catch (e) {
      console.error(e)
    }
  }

  const value = {
    user,
    login,
    logout,
  }

  useIdleTimer({
    timeout: 1000 * 60 * 60 * 2, //logout after 2 hr
    disabled: user === null, // check that if the user has logged in
    onIdle: logout,
  })

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export const useAuth = () => {
  return useContext(AuthContext)
}
